import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // Update the path as needed
import classes from "./AlternateProductForm.module.css";
import { useAlternateProductContext } from "./AlternateProductContext";
import { t } from "i18next";
import { ImageWithModal } from "../Modals";
import { alternateProductLinks } from "../CountryAndLanguage/CountriesData";

export const AlternateProductForm = () => {
  const navigate = useNavigate();
  const { setAlternateProductData, alternateProductData } =
    useAlternateProductContext();

  const [possession, setPossession] = useState<number | "">(
    alternateProductData.quantity || ""
  );
  const [replacementA, setReplacementA] = useState<number | "">(
    () => alternateProductData.replacementProductSwitch || ""
  );
  const [replacementB, setReplacementB] = useState<number | "">(
    () => alternateProductData.replacementProductButton || ""
  );
  const [serialNumber, setSerialNumber] = useState<string | undefined>(
    alternateProductData.serialNumber || ""
  );
  const [reason, setReason] = useState<string | undefined>(
    alternateProductData.noReplacementReason || ""
  );

  const savedCountry = sessionStorage.getItem("country") ?? "";
  const savedCountryCode = sessionStorage.getItem("countryId") ?? "";
  const selctedCountryProductLinks = alternateProductLinks.find(
    (item) => item.countryId === savedCountryCode
  );
  const countriesWithNoContactMeOption = [
    "CH",
    "BE",
    "DE",
    "DK",
    "ES",
    "FR",
    "UK",
    "IT",
    "NL",
    "NO",
    "SE",
    "GL",
  ];
  const hasContactMeToDiscussOption =
    countriesWithNoContactMeOption.includes(savedCountry);
  const isAmerOrApacCountry = [
    "US",
    "CA",
    "AU",
    "JP",
    "KR",
    "NZ",
    "TW",
    "SG",
    "HK",
    "Hant",
  ].includes(savedCountry);

  // selectedOption can be one of the three mutually exclusive options or null
  const [selectedOption, setSelectedOption] = useState<
    "showReplacement" | "contact" | "noAlternate" | null
  >(() => {
    if (alternateProductData.noReplacement) return "noAlternate";
    if (alternateProductData.contactMe) return "contact";
    if (
      alternateProductData.replacementProductSwitch > 0 ||
      alternateProductData.replacementProductButton > 0
    )
      return "showReplacement";
    return null;
  });
  // Validation for replacement totals
  const isReplacementTotalValid =
    possession !== "" &&
    Number(possession) >= 0 &&
    (replacementA !== "" || replacementB !== "")
      ? Number(replacementA) + Number(replacementB) <= Number(possession)
      : true;

  const isSerialNoRequired = isAmerOrApacCountry;
  const serialValue = serialNumber?.trim() || "";
  const isNextDisabled =
    !(
      (selectedOption === "showReplacement" &&
        isReplacementTotalValid &&
        (!isSerialNoRequired || serialValue !== "")) ||
      selectedOption === "contact" ||
      selectedOption === "noAlternate"
    ) || Number(possession) < 0;

  // Handler to change the mutually exclusive checkboxes
  const onOptionChange = (
    option: "showReplacement" | "contact" | "noAlternate"
  ) => {
    if (selectedOption === option) {
      setSelectedOption(null);
      if (option === "showReplacement") {
        setReplacementA("");
        setReplacementB("");
        setSerialNumber("");
      }
    } else {
      setSelectedOption(option);
      // only reset values if changing away from showReplacement
      if (
        selectedOption === "showReplacement" &&
        option !== "showReplacement"
      ) {
        setReplacementA("");
        setReplacementB("");
      }
    }
  };

  const handleNext = () => {
    setAlternateProductData({
      quantity: Number(possession),
      replacementProductSwitch:
        selectedOption === "showReplacement"
          ? replacementA !== ""
            ? Number(replacementA)
            : 0
          : 0,
      replacementProductButton:
        selectedOption === "showReplacement"
          ? replacementB !== ""
            ? Number(replacementB)
            : 0
          : 0,
      noReplacement: selectedOption === "noAlternate",
      contactMe: selectedOption === "contact",
      serialNumber:
        selectedOption === "showReplacement" &&
        (Number(replacementA) > 0 || Number(replacementB) > 0)
          ? serialNumber
          : "",
      noReplacementReason: reason,
    });
    navigate("/user-form");
  };
  const handleBack = () => {
    navigate("/consignee?type=user");
  };
  const isAMERandNoAlternate =
    selectedOption === "noAlternate" &&
    (savedCountry === "US" || savedCountry === "CA");
  return (
    <div
      className={
        (classes.container,
        selectedOption === "showReplacement"
          ? classes.containerWithAlternateOptions
          : classes.container)
      }
    >
      <h3>{t("alternate_product.page_title")}</h3>
      <div className={classes.formWrapper}>
        {/* Number in possession input */}
        <div className={classes.firstRow}>
          <label className={classes.label} htmlFor="quantity">
            {t("alternate_product.number_in_possession")}
          </label>
          <input
            type="number"
            min={0} // <-- Added to prevent negative values
            className={classes.input}
            id="quantity"
            value={possession}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                setPossession(""); // empty string to clear input
              } else {
                const num = Number(val);
                setPossession(num >= 0 ? num : 0); // clamp at 0
              }
            }}
            placeholder={t("alternate_product.quantity")}
          />
        </div>

        {/* Show Replacement Section checkbox */}
        <div className={classes.row}>
          <label className={classes.label}>
            {t("alternate_product.show_replacement_section")}
          </label>
          <div className={classes.checkboxWrapper}>
            <input
              type="checkbox"
              checked={selectedOption === "showReplacement"}
              onChange={() => onOptionChange("showReplacement")}
            />
          </div>
        </div>

        {/* Replacement inputs - only shown if Show Replacement is selected */}
        {selectedOption === "showReplacement" && (
          <>
            <p className={classes.paragraph}>
              {t("alternate_product.replacement_info_text")}
            </p>
            <div className={classes.rowWIthImage}>
              <div className={classes.row}>
                <label className={classes.label}>
                  {t("alternate_product.replace_name", {
                    name: "SwitchControl",
                  })}
                </label>
                <input
                  type="number"
                  min={0} // <-- Added here too
                  className={classes.input}
                  value={replacementA}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setReplacementA(""); // allow empty string when input is cleared
                    } else {
                      const num = Number(val);
                      setReplacementA(num >= 0 ? num : 0); // clamp negative values to 0
                    }
                    setSelectedOption("showReplacement"); // keep this selected
                  }}
                />
              </div>
              <div className={classes.imageDiv}>
                <img
                  src="/images/switch.jpg"
                  alt="SwitchControl"
                  className={classes.switchImages}
                />
                {selctedCountryProductLinks?.switchControlLinks.length &&
                  selctedCountryProductLinks?.switchControlLinks.length > 0 && (
                    <div style={{ display: "column" }}>
                      {selctedCountryProductLinks?.switchControlLinks.map(
                        (link) => {
                          return (
                            <a
                              href={link.link}
                              target="_blank"
                              rel="noreferrer"
                              style={{ display: "block", marginBottom: "7px" }}
                            >
                              {link.label}
                            </a>
                          );
                        }
                      )}
                    </div>
                  )}
              </div>
            </div>
            <div className={classes.rowWIthImage}>
              <div className={classes.row}>
                <label className={classes.label}>
                  {t("alternate_product.replace_name", {
                    name: "SwitchControl w. mono jack and buddy button",
                  })}
                </label>
                <input
                  type="number"
                  min={0} // <-- Added here too
                  className={classes.input}
                  value={replacementB}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setReplacementB(""); // allow clearing input
                    } else {
                      const num = Number(val);
                      setReplacementB(num >= 0 ? num : 0); // clamp negatives to 0
                    }
                    setSelectedOption("showReplacement"); // keep this selected
                  }}
                />
              </div>
              <div className={classes.imageDiv}>
                <img
                  src="/images/switchWithBuddy.jpg"
                  alt="SwitchControl w. mono jack and buddy button"
                  className={classes.switchImages}
                />
                {selctedCountryProductLinks?.buddyButtonLinks.length &&
                  selctedCountryProductLinks?.buddyButtonLinks.length > 0 && (
                    <div style={{ display: "column" }}>
                      {selctedCountryProductLinks?.buddyButtonLinks.map(
                        (link) => {
                          return (
                            <a
                              href={link.link}
                              target="_blank"
                              rel="noreferrer"
                              style={{ display: "block", marginBottom: "7px" }}
                            >
                              {link.label}
                            </a>
                          );
                        }
                      )}
                    </div>
                  )}
              </div>
            </div>

            {!isReplacementTotalValid ||
              (possession === "" && (
                <p className={classes.infoText}>
                  {t("alternate_product.warning_info_text")}
                </p>
              ))}

            <div className={classes.responsiveRow}>
              <div className={classes.row}>
                <label className={classes.label}>
                  {t("alternate_product.smart_drive_serial_number")}{" "}
                  {isAmerOrApacCountry && <span>*</span>}
                </label>
                <input
                  type="text"
                  className={classes.input}
                  value={serialNumber ?? ""}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  placeholder={t("alternate_product.enter_serial_number")}
                  required={isSerialNoRequired}
                />
              </div>

              <ImageWithModal
                src="/images/SD_SerialNumber.jpg"
                alt="Example Image"
              />
            </div>
          </>
        )}

        {/* Contact Me and No Alternate checkboxes */}
        <div className={classes.checkboxGroup}>
          {["contact", "noAlternate"]
            .filter(
              (val) => val !== "contact" || hasContactMeToDiscussOption !== true
            ) // hide "contact" if not allowed
            .map((val) => (
              <div className={classes.row} key={val}>
                <label className={classes.label}>
                  {val === "contact"
                    ? t("alternate_product.contact_me_to_discuss")
                    : t("alternate_product.no_alternate_required")}
                </label>
                <div className={classes.checkboxWrapper}>
                  <input
                    type="checkbox"
                    checked={selectedOption === val}
                    onChange={() =>
                      onOptionChange(val as "contact" | "noAlternate")
                    }
                  />
                </div>
              </div>
            ))}
        </div>
        {isAMERandNoAlternate && (
          <div className={classes.firstRow}>
            <label className={classes.label}>
              {t("alternate_product.reason")}
            </label>
            <textarea
              className={classes.input}
              value={reason}
              onChange={(e) => setReason(e.target.value || undefined)}
              placeholder={t("alternate_product.reason_placeholder")}
            />
          </div>
        )}

        <p className={classes.infoText}>
          {t("alternate_product.madatory_info")}*
        </p>
        <div className={"mandatoryText"}>* {t("common.required")}</div>
        <div className={classes.submitContainer}>
          <button className={classes.backButton} onClick={handleBack}>
            {t("common.back")}
          </button>
          <button
            className={classes.submitButton}
            disabled={isNextDisabled || (isAMERandNoAlternate && !reason)}
            onClick={handleNext}
          >
            {t("common.next")}
          </button>
        </div>
      </div>
    </div>
  );
};
